/**
 * MultiSeriesWebGLChart
 *
 * Sibling to WebGLChart for plots that combine a base time-series line with
 * one or more scatter overlays (e.g. anomaly markers on top of a trend line).
 * Renders with the same webgl-plot pipeline so it visually matches the rest
 * of the app: same axis labels, same zoom/pan, same tooltip, same compact
 * card chrome. Participates in the `sharedBrushRange` sync pattern so it can
 * be locked to sibling WebGLChart instances inside the threshold modal.
 *
 * Scope is intentionally narrow: tooltip tracks the FIRST line series only
 * (matching the original AnomalyOverlayChart UX); scatter markers are
 * visual-only and rendered via ScatterOverlay.
 */
import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { WebglPlot, WebglLine, ColorRGBA } from 'webgl-plot';

import { Card, CardContent } from '@/shared/components/ui/card';
import { CHART_HEIGHT, CHART_PADDING, LOCALE } from './constants';
import { ZoomState, BrushRange, Bounds } from './types';
import { useInteraction, DragSelection, HoverPosition } from './hooks';
import { visibleXRange, visibleYRange, fitScaleOffsetY } from './utils/zoomFit';
import { AxisOverlay } from './overlays/AxisOverlay';
import { SelectionOverlay } from './overlays/SelectionOverlay';
import { ScatterOverlay } from './overlays/ScatterOverlay';
import { TooltipOverlay, TooltipData } from './overlays/TooltipOverlay';
import { ChartControls } from './components';

const DEFAULT_LINE_COLOR = new ColorRGBA(0.082, 0.722, 0.651, 1); // #14B8A6

function hexToColorRGBA(hex: string | undefined): ColorRGBA {
  if (!hex) return DEFAULT_LINE_COLOR;
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return DEFAULT_LINE_COLOR;
  const n = parseInt(m[1], 16);
  return new ColorRGBA(
    ((n >> 16) & 0xff) / 255,
    ((n >> 8) & 0xff) / 255,
    (n & 0xff) / 255,
    1,
  );
}

export interface MultiSeriesPoint {
  /** Backend timestamp — accepts ISO string or epoch-ms number (matches PlotTrace.x). */
  x: string | number;
  y: number | null;
}

export interface MultiSeriesSpec {
  label: string;
  mode: 'line' | 'scatter';
  /** Hex color (e.g. "#14B8A6"); falls back to teal (line) or red (scatter). */
  color?: string;
  data: MultiSeriesPoint[];
}

interface ProcessedSeries {
  label: string;
  mode: 'line' | 'scatter';
  color: string;
  timestamps: number[];      // seconds since epoch
  values: (number | null)[]; // aligned with timestamps
}

export interface MultiSeriesWebGLChartProps {
  series: MultiSeriesSpec[];
  /** Y-axis label (e.g. "Q_RGK [kW]"). */
  yLabel: string;
  /** Total chart height in px; defaults to CHART_HEIGHT (400). */
  height?: number;
  /** Hide built-in ChartControls (Reset Zoom button + point count). Defaults
   *  to `false` — set to `true` when the parent provides its own reset UI
   *  (e.g. the threshold modal's modal-level Reset Zoom button). */
  hideControls?: boolean;
  sharedBrushRange?: BrushRange | null;
  onSharedBrushChange?: (range: BrushRange | null) => void;
}

function parseTimestampSeconds(x: string | number): number {
  if (typeof x === 'number') return x / 1000;
  const ms = new Date(x).getTime();
  return Number.isFinite(ms) ? ms / 1000 : NaN;
}

function processSeries(s: MultiSeriesSpec): ProcessedSeries {
  const timestamps: number[] = [];
  const values: (number | null)[] = [];
  for (const p of s.data) {
    const ts = parseTimestampSeconds(p.x);
    if (!Number.isFinite(ts)) continue;
    timestamps.push(ts);
    const v = p.y;
    if (v === null || v === undefined || !Number.isFinite(v)) {
      values.push(null);
    } else {
      values.push(v);
    }
  }
  const defaultColor = s.mode === 'scatter' ? '#ef4444' : '#14B8A6';
  return {
    label: s.label,
    mode: s.mode,
    color: s.color ?? defaultColor,
    timestamps,
    values,
  };
}

const MultiSeriesWebGLChartImpl: React.FC<MultiSeriesWebGLChartProps> = ({
  series,
  yLabel,
  height,
  hideControls = false,
  sharedBrushRange,
  onSharedBrushChange,
}) => {
  const { t } = useTranslation('charts');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const wglpRef = useRef<WebglPlot | null>(null);
  const initialBoundsRef = useRef<Bounds | null>(null);
  const lastSentRangeRef = useRef<BrushRange | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const [containerWidth, setContainerWidth] = useState(800);
  const [dragSelection, setDragSelection] = useState<DragSelection | null>(null);
  const [hoverPosition, setHoverPosition] = useState<HoverPosition | null>(null);
  const [zoomState, setZoomState] = useState<ZoomState>({
    scaleX: 1,
    scaleY: 1,
    offsetX: 0,
    offsetY: 0,
    xMin: 0,
    xMax: 1,
  });

  const effectiveHeight = height ?? CHART_HEIGHT;
  const chartAreaWidth = Math.max(0, containerWidth - CHART_PADDING.left - CHART_PADDING.right);
  const chartAreaHeight = effectiveHeight - CHART_PADDING.top - CHART_PADDING.bottom;

  // Process every series; compute global x/y bounds across line + scatter so
  // scatter markers near the edges are not clipped from the y-axis.
  const processed = useMemo(() => series.map(processSeries), [series]);

  const bounds = useMemo<Bounds>(() => {
    let xMin = Infinity;
    let xMax = -Infinity;
    let yMin = Infinity;
    let yMax = -Infinity;
    for (const s of processed) {
      for (let i = 0; i < s.timestamps.length; i++) {
        const t = s.timestamps[i];
        if (Number.isFinite(t)) {
          if (t < xMin) xMin = t;
          if (t > xMax) xMax = t;
        }
        const v = s.values[i];
        if (v !== null && Number.isFinite(v)) {
          if (v < yMin) yMin = v;
          if (v > yMax) yMax = v;
        }
      }
    }
    if (xMin === Infinity) { xMin = 0; xMax = 1; }
    if (yMin === Infinity) { yMin = 0; yMax = 1; }
    return { xMin, xMax, yMin, yMax };
  }, [processed]);

  // The first line series feeds the tooltip; if there are no line series, no
  // tooltip is shown.
  const tooltipSeries = useMemo(
    () => processed.find((s) => s.mode === 'line') ?? null,
    [processed],
  );

  // Total point count across all series — surfaced in ChartControls.
  const totalPoints = useMemo(
    () => processed.reduce((sum, s) => sum + s.timestamps.length, 0),
    [processed],
  );

  // Reset zoom.
  //
  // When a parent provides `onSharedBrushChange`, broadcast `null` and let the
  // sharedBrushRange effect on each instance (including this one) reset every
  // chart to ITS OWN initial bounds — a true group reset even when siblings
  // span different x-windows. Without a parent, reset locally.
  const handleResetZoom = React.useCallback(() => {
    if (onSharedBrushChange) {
      onSharedBrushChange(null);
      return;
    }
    if (!initialBoundsRef.current) return;
    const { xMin, xMax } = initialBoundsRef.current;
    setZoomState({
      scaleX: 1,
      scaleY: 1,
      offsetX: 0,
      offsetY: 0,
      xMin,
      xMax,
    });
  }, [onSharedBrushChange]);

  // Initialise WebGL lines from line series whenever data or geometry changes.
  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    if (canvas.width === 0 || canvas.height === 0) return;

    if (wglpRef.current) {
      wglpRef.current.removeAllLines();
    }

    initialBoundsRef.current = bounds;
    const { xMin, xMax, yMin, yMax } = bounds;
    const xRange = xMax - xMin || 1;
    const yRangeRaw = yMax - yMin || 1;
    const yPadding = yRangeRaw * 0.05;
    const yMinPadded = yMin - yPadding;
    const yMaxPadded = yMax + yPadding;
    const yRangePadded = yMaxPadded - yMinPadded;

    try {
      const wglp = new WebglPlot(canvas);
      wglpRef.current = wglp;

      for (const s of processed) {
        if (s.mode !== 'line') continue;
        if (s.timestamps.length === 0) continue;

        const line = new WebglLine(hexToColorRGBA(s.color), s.timestamps.length);
        for (let i = 0; i < s.timestamps.length; i++) {
          const normalizedX = ((s.timestamps[i] - xMin) / xRange) * 2 - 1;
          line.setX(i, normalizedX);
          const v = s.values[i];
          if (v !== null && Number.isFinite(v)) {
            const normalizedY = ((v - yMinPadded) / yRangePadded) * 2 - 1;
            line.setY(i, normalizedY);
          } else {
            line.setY(i, NaN);
          }
        }
        wglp.addLine(line);
      }

      wglp.gScaleX = zoomState.scaleX;
      wglp.gScaleY = zoomState.scaleY;
      wglp.gOffsetX = zoomState.offsetX;
      wglp.gOffsetY = zoomState.offsetY;
      wglp.update();
    } catch {
      // WebGL init failure — silent (matches WebGLChart behaviour)
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (wglpRef.current) {
        wglpRef.current.removeAllLines();
      }
      wglpRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processed, bounds.yMin, bounds.yMax, chartAreaWidth, chartAreaHeight]);

  // Reflect zoom changes onto the WebGL plot.
  useEffect(() => {
    if (!wglpRef.current) return;
    wglpRef.current.gScaleX = zoomState.scaleX;
    wglpRef.current.gScaleY = zoomState.scaleY;
    wglpRef.current.gOffsetX = zoomState.offsetX;
    wglpRef.current.gOffsetY = zoomState.offsetY;
    wglpRef.current.update();
  }, [zoomState]);

  // Compute a y-fit (scaleY/offsetY) from the visible-x window, scanning ALL
  // series (line + scatter) so anomaly markers near the edges are not
  // clipped when refitting.
  const computeYFit = useCallback(
    (scaleX: number, offsetX: number) => {
      const b = initialBoundsRef.current;
      if (!b) return null;
      const { xMin, xMax } = visibleXRange(b.xMin, b.xMax, scaleX, offsetX);
      let lo = Infinity, hi = -Infinity;
      for (const s of processed) {
        const r = visibleYRange(s.timestamps, s.values, xMin, xMax);
        if (r) { if (r.yMin < lo) lo = r.yMin; if (r.yMax > hi) hi = r.yMax; }
      }
      if (lo === Infinity) return null;
      return fitScaleOffsetY(lo, hi, b.yMin, b.yMax);
    },
    [processed],
  );

  // Hook up pan / drag-select / wheel-zoom / hover.
  useInteraction({
    canvasRef,
    zoomState,
    onZoomChange: setZoomState,
    initialBounds: initialBoundsRef,
    onSharedBrushChange,
    lastSentRangeRef,
    onDragSelectionChange: setDragSelection,
    onHoverChange: setHoverPosition,
    computeYFit,
  });

  // Sync FROM siblings via sharedBrushRange (mirror of WebGLChart's effect at
  // WebGLChart.tsx:108-160).
  useEffect(() => {
    if (!initialBoundsRef.current) return;

    if (!sharedBrushRange) {
      setZoomState({
        scaleX: 1,
        scaleY: 1,
        offsetX: 0,
        offsetY: 0,
        xMin: initialBoundsRef.current.xMin,
        xMax: initialBoundsRef.current.xMax,
      });
      return;
    }

    if (lastSentRangeRef.current) {
      const same =
        Math.abs(lastSentRangeRef.current.startTime - sharedBrushRange.startTime) < 1 &&
        Math.abs(lastSentRangeRef.current.endTime - sharedBrushRange.endTime) < 1;
      if (same) {
        lastSentRangeRef.current = null;
        return;
      }
    }

    const { xMin, xMax } = initialBoundsRef.current;
    const fullRange = xMax - xMin;
    const visibleStart = sharedBrushRange.startTime / 1000;
    const visibleEnd = sharedBrushRange.endTime / 1000;
    const visibleRange = visibleEnd - visibleStart;
    if (!Number.isFinite(visibleRange) || visibleRange <= 0) return;

    const newScaleX = fullRange / visibleRange;
    const visibleCenter = (visibleStart + visibleEnd) / 2;
    const dataCenter = (xMin + xMax) / 2;
    const centerOffset = (dataCenter - visibleCenter) / fullRange;
    const newOffsetX = centerOffset * 2 * newScaleX;

    const yFit = computeYFit(newScaleX, newOffsetX);
    setZoomState((prev) => ({
      ...prev,
      scaleX: newScaleX,
      offsetX: newOffsetX,
      ...(yFit ? { scaleY: yFit.scaleY, offsetY: yFit.offsetY } : {}),
    }));
  }, [sharedBrushRange, computeYFit]);

  // Resize handling (mirror WebGLChart.tsx:163-194).
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const updateCanvasDimensions = (width: number) => {
      const canvasWidth = Math.max(100, width - CHART_PADDING.left - CHART_PADDING.right);
      const canvasHeight = effectiveHeight - CHART_PADDING.top - CHART_PADDING.bottom;
      canvas.width = canvasWidth * window.devicePixelRatio;
      canvas.height = canvasHeight * window.devicePixelRatio;
      canvas.style.width = `${canvasWidth}px`;
      canvas.style.height = `${canvasHeight}px`;
      setContainerWidth(width);
    };

    updateCanvasDimensions(container.clientWidth || 800);
    const observer = new ResizeObserver((entries) => {
      for (const e of entries) updateCanvasDimensions(e.contentRect.width);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [effectiveHeight]);

  // Tooltip data — looks up the nearest sample in the FIRST line series at the
  // hovered x. Scatter series do not participate in the tooltip.
  const tooltipData = useMemo((): TooltipData | null => {
    if (!hoverPosition || !initialBoundsRef.current || !tooltipSeries) return null;
    const { timestamps, values } = tooltipSeries;
    if (timestamps.length === 0) return null;

    const { xMin, xMax } = initialBoundsRef.current;
    if (!Number.isFinite(xMin) || !Number.isFinite(xMax) || xMin >= xMax) return null;

    const xRange = xMax - xMin;
    const hoverTs = ((hoverPosition.normX + 1) / 2) * xRange + xMin;

    let left = 0;
    let right = timestamps.length - 1;
    while (left < right) {
      const mid = Math.floor((left + right) / 2);
      if (timestamps[mid] < hoverTs) left = mid + 1;
      else right = mid;
    }

    const idx = left;
    const t = timestamps[idx];
    const v = values[idx];
    if (v === null) return null;

    const yRange = bounds.yMax - bounds.yMin || 1;
    const yPad = yRange * 0.05;
    const yMinPad = bounds.yMin - yPad;
    const yMaxPad = bounds.yMax + yPad;

    const normalizedX = ((t - xMin) / xRange) * 2 - 1;
    const zoomedX = normalizedX * zoomState.scaleX + zoomState.offsetX;
    const normalizedY = ((v - yMinPad) / (yMaxPad - yMinPad)) * 2 - 1;
    const zoomedY = normalizedY * zoomState.scaleY + zoomState.offsetY;

    return {
      x: ((zoomedX + 1) / 2) * chartAreaWidth,
      y: ((1 - zoomedY) / 2) * chartAreaHeight,
      timestamp: t,
      value: v,
    };
  }, [hoverPosition, tooltipSeries, bounds, zoomState, chartAreaWidth, chartAreaHeight]);

  const formatTime = (tsSeconds: number) => {
    const d = new Date(tsSeconds * 1000);
    return new Intl.DateTimeFormat(LOCALE, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  };

  return (
    <Card className="w-full">
      <CardContent className="pt-0">
        {!hideControls && (
          <>
            <ChartControls
              totalPoints={totalPoints}
              onResetZoom={handleResetZoom}
            />

            {/* Time range */}
            <div className="text-center text-xs sm:text-sm text-muted-foreground mb-1.5 sm:mb-2">
              {t('showAllData.timeRange', { ns: 'dataAdjustments', defaultValue: 'Time Range' })}:{' '}
              {formatTime(bounds.xMin)} – {formatTime(bounds.xMax)}
            </div>

            {/* Zoom hint */}
            <div className="text-[10px] sm:text-xs text-muted-foreground text-center mb-1.5 sm:mb-2">
              {t('dataChart.zoomHint', 'Scroll to zoom, drag to select area, double-click to reset')}
            </div>
          </>
        )}
        <div
          ref={containerRef}
          className="relative w-full"
          style={{ height: effectiveHeight }}
        >
          <canvas
            ref={canvasRef}
            className="absolute"
            style={{
              touchAction: 'none',
              left: CHART_PADDING.left,
              top: CHART_PADDING.top,
              width: chartAreaWidth,
              height: chartAreaHeight,
            }}
          />
          <AxisOverlay
            width={containerWidth}
            height={effectiveHeight}
            xMin={bounds.xMin}
            xMax={bounds.xMax}
            yMin={bounds.yMin}
            yMax={bounds.yMax}
            zoomState={zoomState}
            seriesName={yLabel}
          />
          {dragSelection && (
            <SelectionOverlay
              startX={dragSelection.startX}
              endX={dragSelection.endX}
              startY={dragSelection.startY}
              endY={dragSelection.endY}
              chartHeight={chartAreaHeight}
              active={dragSelection.active}
            />
          )}
          {processed
            .filter((s) => s.mode === 'scatter')
            .map((s, i) => (
              <ScatterOverlay
                key={`scatter-${i}`}
                points={s.timestamps.map((t, idx) => ({ t, v: s.values[idx] }))}
                color={s.color}
                xMin={bounds.xMin}
                xMax={bounds.xMax}
                yMin={bounds.yMin}
                yMax={bounds.yMax}
                zoomState={zoomState}
                chartWidth={chartAreaWidth}
                chartHeight={chartAreaHeight}
              />
            ))}
          <TooltipOverlay data={tooltipData} chartHeight={chartAreaHeight} />
        </div>
      </CardContent>
    </Card>
  );
};

export const MultiSeriesWebGLChart = React.memo(MultiSeriesWebGLChartImpl);
export default MultiSeriesWebGLChart;
